import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { apiFetch, apiDownload } from '../api';
import { formatEuro, formatearTamano } from '../utils/helpers';
import {
  Eye, Trash2, ExternalLink, ClipboardList, Check, X, UserRound, Download, FileText, Paperclip,
} from 'lucide-react';
import { useCollaborations } from '../context/CollaborationsContext';
import { Card, CardContent } from './ui/card';
import { Button, buttonVariants } from './ui/button';
import { cn } from '../lib/utils';
import { detalleProducto } from '../lib/detalleProducto';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

const SECTIONS = [
  {
    title: 'Perfil',
    fields: [
      ['usuario', 'Usuario'],
      ['plataforma', 'Plataforma'],
      ['audiencia', 'Audiencia'],
      ['factura', 'Factura'],
    ],
  },
  {
    title: 'Datos personales',
    fields: [
      ['nombre', 'Nombre'],
      ['dni', 'DNI'],
      ['email', 'Email'],
      ['telefono', 'Teléfono'],
    ],
  },
  {
    title: 'Dirección de envío',
    fields: [
      ['direccion', 'Dirección'],
      ['codigo_postal', 'Código postal'],
      ['ciudad', 'Ciudad'],
      ['provincia', 'Provincia'],
    ],
  },
  {
    title: 'Dirección de facturación',
    // Solo se rellena cuando difiere de la de envío, así que la ocultamos
    // entera si viene vacía en vez de mostrar cuatro guiones.
    hideIfEmpty: true,
    fields: [
      ['direccion_facturacion', 'Dirección'],
      ['codigo_postal_facturacion', 'Código postal'],
      ['ciudad_facturacion', 'Ciudad'],
      ['provincia_facturacion', 'Provincia'],
    ],
  },
  {
    title: 'Colaboración',
    fields: [['codigo_descuento', 'Código de descuento']],
  },
];

/**
 * Perfil público del influencer. Instagram va sin arroba en la ruta, y TikTok
 * y YouTube con ella; el usuario se guarda con arroba, así que hay que quitarla.
 * Igual que en server/lib/solicitudMail.js.
 */
function urlPerfil(form) {
  const usuario = String(form.usuario || '').replace(/^@+/, '');
  if (!usuario) return null;
  if (form.plataforma === 'TikTok') return `https://www.tiktok.com/@${usuario}`;
  if (form.plataforma === 'YouTube') return `https://www.youtube.com/@${usuario}`;
  return `https://www.instagram.com/${usuario}`;
}

async function descargarAdjunto(form, adjunto) {
  try {
    await apiDownload(`/api/forms/${form.id}/adjuntos/${adjunto.id}`, adjunto.nombre);
  } catch (err) {
    toast.error(`No se pudo descargar: ${err.message}`);
  }
}

/** Los archivos que adjuntó el influencer, con un botón para bajar cada uno. */
function Adjuntos({ form }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold pb-1.5 border-b">
        Archivos adjuntos ({form.adjuntos.length})
      </div>
      {form.adjuntos.map((adjunto) => (
        <div key={adjunto.id} className="flex items-center gap-3 py-2 border-b last:border-b-0">
          <FileText className="h-4 w-4 shrink-0 text-primary" />
          <div className="min-w-0 flex-1 truncate text-sm" title={adjunto.nombre}>
            {adjunto.nombre}
          </div>
          <div className="shrink-0 text-xs text-muted-foreground">{formatearTamano(adjunto.size)}</div>
          <Button variant="outline" size="sm" onClick={() => descargarAdjunto(form, adjunto)}>
            <Download className="h-3.5 w-3.5" />
            Descargar
          </Button>
        </div>
      ))}
    </div>
  );
}

const ESTADOS = {
  aceptada: { texto: 'Aceptada', clase: 'bg-green-100 text-green-700' },
  denegada: { texto: 'Denegada', clase: 'bg-muted text-muted-foreground' },
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function FieldRow({ label, value }) {
  const isUrl = typeof value === 'string' && /^https?:\/\//i.test(value);
  return (
    <div className="grid grid-cols-3 gap-3 py-2 border-b last:border-b-0">
      <div className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
        {label}
      </div>
      <div className="col-span-2 text-sm break-words">
        {value ? (
          isUrl ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline inline-flex items-center gap-1"
            >
              {value}
              <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            value
          )
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </div>
    </div>
  );
}

function FormDetailDialog({ form, onClose, onEliminar }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{form.usuario || form.nombre || 'Formulario'}</DialogTitle>
          <DialogDescription>Recibido el {formatDate(form.createdAt)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {form.productos?.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold pb-1.5 border-b">
                Productos elegidos ({form.productos.length})
              </div>
              {form.productos.map((p, i) => (
                <div key={`${p.productId}-${p.opId}-${i}`} className="flex items-center gap-3 py-2 border-b last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">{p.nombre}</div>
                    <div className="text-xs text-muted-foreground">
                      {detalleProducto(p)}
                    </div>
                  </div>
                  <div className="shrink-0 text-sm font-semibold">
                    {p.precio != null ? formatEuro(p.precio) : '—'}
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-3 pt-2 border-t">
                <div className="min-w-0 flex-1 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                  Total
                </div>
                <div className="shrink-0 text-sm font-semibold">
                  {formatEuro(
                    form.productos.reduce((suma, p) => suma + (p.precio || 0), 0),
                  )}
                </div>
              </div>
            </div>
          )}

          {form.adjuntos?.length > 0 && <Adjuntos form={form} />}

          {SECTIONS.filter(
            (section) =>
              !section.hideIfEmpty || section.fields.some(([key]) => form[key]),
          ).map((section) => (
            <div key={section.title} className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold pb-1.5 border-b">
                {section.title}
              </div>
              {section.fields.map(([key, label]) => (
                <FieldRow key={key} label={label} value={form[key]} />
              ))}
            </div>
          ))}

          {/* Eliminar vive aquí y no en la tarjeta: es irreversible y no debe
              quedar al lado de los botones que se usan a diario. */}
          <Button variant="destructive" onClick={onEliminar} className="w-full">
            <Trash2 className="h-4 w-4" />
            Eliminar solicitud
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FormulariosPage() {
  const { insertCollaboration } = useCollaborations();
  const [forms, setForms] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [resolviendo, setResolviendo] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch('/api/forms');
      setForms(Array.isArray(data) ? data : []);
    } catch {
      toast.error('No se pudieron cargar los formularios');
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  async function handleDelete(id) {
    if (!window.confirm('¿Eliminar esta solicitud? No se puede deshacer.')) return;
    try {
      await apiFetch(`/api/forms/${id}`, { method: 'DELETE' });
      setForms((prev) => prev.filter((f) => f.id !== id));
      setSelected(null);
      toast.success('Solicitud eliminada');
    } catch {
      toast.error('Error al eliminar la solicitud');
    }
  }

  function reemplazar(solicitud) {
    setForms((prev) => prev.map((f) => (f.id === solicitud.id ? solicitud : f)));
  }

  async function handleAceptar(form) {
    setResolviendo(form.id);
    try {
      const { solicitud, colaboracion } = await apiFetch(`/api/forms/${form.id}/aceptar`, {
        method: 'POST',
      });
      reemplazar(solicitud);
      // Que aparezca en Influencers sin recargar.
      insertCollaboration(colaboracion);
      toast.success(`Colaboración creada con ${colaboracion.usuario || colaboracion.name}`);
    } catch (err) {
      toast.error(`No se pudo aceptar: ${err.message}`);
    } finally {
      setResolviendo(null);
    }
  }

  async function handleDenegar(form) {
    setResolviendo(form.id);
    try {
      const { solicitud } = await apiFetch(`/api/forms/${form.id}/denegar`, { method: 'POST' });
      reemplazar(solicitud);
      toast.success('Solicitud denegada');
    } catch (err) {
      toast.error(`No se pudo denegar: ${err.message}`);
    } finally {
      setResolviendo(null);
    }
  }

  const q = search.trim().toLowerCase();
  const filtered = q
    ? forms.filter((f) =>
        [f.usuario, f.nombre, f.email, f.codigo_descuento, f.plataforma]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      )
    : forms;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Formularios</h1>
          <p className="text-muted-foreground text-sm">
            Solicitudes de colaboración
          </p>
        </div>
        <a
          href="/formulario"
          target="_blank"
          rel="noopener noreferrer"
          className={cn(buttonVariants({ variant: 'outline' }), 'no-underline')}
          title="Abrir el formulario que rellenan los influencers"
        >
          <ExternalLink className="h-4 w-4" />
          Ver formulario
        </a>
      </div>

      <div className="relative">
        <Input
          type="text"
          placeholder="Buscar solicitud..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <Card className="border-dashed">
          <div className="flex flex-col items-center justify-center text-center py-16 px-6">
            <div className="h-12 w-12 rounded-full bg-muted text-muted-foreground flex items-center justify-center mb-4">
              <ClipboardList className="h-6 w-6" />
            </div>
            <h3 className="text-base font-semibold">
              {q ? 'Sin resultados' : 'Aún no hay formularios'}
            </h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm">
              {q
                ? `Ningún formulario coincide con "${search}"`
                : 'Cuando alguien rellene el formulario público aparecerá aquí.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((form) => {
            // Aceptada o denegada: la decisión ya está tomada.
            const resuelta = Boolean(ESTADOS[form.estado]);
            return (
            <Card key={form.id}>
              <CardContent className="p-4 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">
                      {form.usuario || form.nombre || 'Sin usuario'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(form.createdAt)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {ESTADOS[form.estado] && (
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
                          ESTADOS[form.estado].clase,
                        )}
                      >
                        {ESTADOS[form.estado].texto}
                      </span>
                    )}
                    {form.plataforma && (
                      <span className="inline-flex items-center rounded-full bg-accent text-accent-foreground px-2 py-0.5 text-[11px] font-semibold">
                        {form.plataforma}
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-1 text-xs text-muted-foreground">
                  {form.nombre && (
                    <div>
                      <span className="text-foreground">{form.nombre}</span>
                    </div>
                  )}
                  {form.email && <div className="truncate">{form.email}</div>}
                  {form.codigo_descuento && (
                    <div>
                      Código:{' '}
                      <span className="font-mono text-foreground">
                        {form.codigo_descuento}
                      </span>
                    </div>
                  )}
                  {form.adjuntos?.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Paperclip className="h-3 w-3" />
                      {form.adjuntos.length === 1
                        ? '1 archivo adjunto'
                        : `${form.adjuntos.length} archivos adjuntos`}
                    </div>
                  )}
                </div>

                <div className="mt-auto">
                  {/* Decidida la solicitud, los botones de decidir sobran y se
                      pliegan en vez de desaparecer de golpe.

                      Se pliega con `grid-rows-[1fr] → [0fr]`, que es la forma
                      de animar hasta un alto automático: ni `height: auto` se
                      puede interpolar, ni animar la salida con AnimatePresence
                      sobrevive al re-render que provoca recargar la lista al
                      resolver. El bloque sigue montado, pero oculto y sin foco. */}
                  <div
                    className="grid transition-all duration-300 ease-in-out"
                    style={{
                      gridTemplateRows: resuelta ? '0fr' : '1fr',
                      opacity: resuelta ? 0 : 1,
                      marginBottom: resuelta ? 0 : 8,
                    }}
                    aria-hidden={resuelta}
                  >
                    {/* `min-h-0` es imprescindible: sin él el hijo no puede
                        encogerse por debajo de su contenido y la fila de 0fr
                        no llega a plegarse. */}
                    <div className="grid grid-cols-2 gap-2 overflow-hidden min-h-0">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={resuelta || resolviendo === form.id}
                        onClick={() => handleDenegar(form)}
                      >
                        <X className="h-3.5 w-3.5" />
                        Denegar
                      </Button>
                      <Button
                        size="sm"
                        disabled={resuelta || resolviendo === form.id}
                        onClick={() => handleAceptar(form)}
                      >
                        <Check className="h-3.5 w-3.5" />
                        Aceptar
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {urlPerfil(form) ? (
                      <a
                        href={urlPerfil(form)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          buttonVariants({ variant: 'outline', size: 'sm' }),
                          'no-underline',
                        )}
                      >
                        <UserRound className="h-3.5 w-3.5" />
                        Ver perfil
                      </a>
                    ) : (
                      <Button variant="outline" size="sm" disabled>
                        <UserRound className="h-3.5 w-3.5" />
                        Ver perfil
                      </Button>
                    )}

                    <Button variant="outline" size="sm" onClick={() => setSelected(form)}>
                      <Eye className="h-3.5 w-3.5" />
                      Ver detalles
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {selected && (
        <FormDetailDialog
          form={selected}
          onClose={() => setSelected(null)}
          onEliminar={() => handleDelete(selected.id)}
        />
      )}
    </div>
  );
}
